import React, { useState, useEffect } from 'react';
import { ncService } from '../services/api';
import { showError, showSuccess } from '../utils/toast';
import PageHeader from './ui/PageHeader';
import Card from './ui/Card';
import StateBlock from './ui/StateBlock';
import Button from './ui/Button';
import { formatCurrencyBR } from '../utils/currency';
import './DashboardPage.css';

// #52 (Sessão 33): Dashboard MVP. 3 blocos a partir do GET /cliente/{id}/painel.
// #124 (Sessão 35): piloto da fundação — usa PageHeader/Card/StateBlock/Button +
// Material Symbols (em vez de emoji). Comportamento idêntico ao #52.

const ORIGEM_CFG = {
  fornecedor: { label: 'Outbound', cls: 'nc-origem-fornecedor' },
  cliente: { label: 'Cliente externo', cls: 'nc-origem-cliente' },
  proprio: { label: 'Própria', cls: 'nc-origem-proprio' },
};

// #160: painel personalizável — esconder/mostrar quadros. Preferência por
// DISPOSITIVO (localStorage), só on/off (sem reorder). BLOCOS_CFG lista os
// quadros toggláveis (hero "NCs aguardando ação" e "Últimas NCs" ficam SEMPRE
// visíveis — não entram aqui). A chave guarda { id: true } = quadro OCULTO →
// ausência da chave = tudo visível = painel idêntico ao de hoje (fail-safe).
const BLOCOS_CFG = [
  { id: 'prontidao',    label: 'Prontidão para auditoria' },
  { id: 'eficacia',     label: 'Eficácias a avaliar' },
  { id: 'financeiro',   label: 'Impacto financeiro' },
  { id: 'fornecedores', label: 'Concentração de fornecedores' },
  { id: 'impacto',      label: 'Impacto por categoria' },
  { id: 'causas',       label: 'Causas mais frequentes' },
  { id: 'sgq',          label: 'Alterações no SGQ e Riscos' },
];
const PREF_BLOCOS_KEY = 'qaasa_dashboard_blocos'; // { id: true } = OCULTO

// #237: rótulo da faixa do selo de prontidão (verde/âmbar/vermelho).
const PRONTIDAO_FAIXA_TXT = {
  verde: 'Pronto',
  ambar: 'Lacunas a fechar',
  vermelho: 'Exposição — priorize',
};

function tempoRelativo(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const dias = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (dias <= 0) return 'hoje';
  if (dias === 1) return 'ontem';
  if (dias < 30) return `há ${dias} dias`;
  const meses = Math.floor(dias / 30);
  if (meses < 12) return meses <= 1 ? 'há 1 mês' : `há ${meses} meses`;
  const anos = Math.floor(meses / 12);
  return anos <= 1 ? 'há 1 ano' : `há ${anos} anos`;
}

function OrigemBadge({ nc }) {
  const origem = nc.origem_tipo || 'proprio';
  const cfg = ORIGEM_CFG[origem] || ORIGEM_CFG.proprio;
  let sufixo = '';
  if (origem === 'fornecedor' && nc.fornecedor_nome) sufixo = ` · ${nc.fornecedor_nome}`;
  else if (origem === 'cliente' && nc.cliente_emissor) sufixo = ` · ${nc.cliente_emissor}`;
  return <span className={`nc-origem-badge ${cfg.cls}`}>{cfg.label}{sufixo}</span>;
}

// #233: dados FICTÍCIOS pra o "demo mode" — conta nova/vazia vê o painel
// preenchido pra perceber o valor antes de acumular dado real. SEMPRE atrás do
// banner "Demonstração" (não-enganar). numero_nc "DEMO-xxx" deixa explícito.
const _demoDias = (dias) => new Date(Date.now() - dias * 86400000).toISOString();
const DEMO_DATA = {
  pendentes_total: 7,
  criticas: 2,
  em_atraso: 1,
  ultimas_5: [
    { id: -1, numero_nc: 'DEMO-018', descricao: 'Vazamento na conexão hidráulica da prensa 3', origem_tipo: 'proprio', created_at: _demoDias(1) },
    { id: -2, numero_nc: 'DEMO-017', descricao: 'Lote de embalagens com impressão fora do padrão', origem_tipo: 'fornecedor', fornecedor_nome: 'Embalagens Sul', created_at: _demoDias(3) },
    { id: -3, numero_nc: 'DEMO-016', descricao: 'Peça usinada fora da tolerância dimensional', origem_tipo: 'proprio', created_at: _demoDias(6) },
    { id: -4, numero_nc: 'DEMO-015', descricao: 'Atraso na inspeção de recebimento do cliente', origem_tipo: 'cliente', cliente_emissor: 'Auto Peças MG', created_at: _demoDias(9) },
  ],
  top_5_fornecedores: [
    { id: -11, nome: 'Aços Vale Verde', qtd: 8 },
    { id: -12, nome: 'Embalagens Sul', qtd: 5 },
    { id: -13, nome: 'Usinagem Precisa', qtd: 3 },
  ],
  financeiro: {
    custo_total: 47800,
    ncs_com_custo: 12,
    ncs_total_periodo: 28,
    mais_caras: [
      { id: -1, numero_nc: 'DEMO-009', descricao: 'Refugo de lote por contaminação de matéria-prima', custo_estimado: 18500, origem_tipo: 'fornecedor', fornecedor_nome: 'Aços Vale Verde' },
      { id: -2, numero_nc: 'DEMO-012', descricao: 'Retrabalho de soldagem em estrutura', custo_estimado: 12300, origem_tipo: 'proprio' },
      { id: -3, numero_nc: 'DEMO-006', descricao: 'Devolução de cliente por dimensional', custo_estimado: 9000, origem_tipo: 'cliente', cliente_emissor: 'Auto Peças MG' },
    ],
  },
  eficacia: { total: 4, vencidas: 1 },
  impacto: {
    contagem: { financeiro: 9, reputacional: 4, operacional: 11 },
    ncs_com_impacto: 18,
    ncs_total_periodo: 28,
  },
  causas: {
    pareto: [
      { pilar: 'Método', qtd: 11, pct: 39.3, pct_acumulado: 39.3 },
      { pilar: 'Máquina', qtd: 8, pct: 28.6, pct_acumulado: 67.9 },
      { pilar: 'Material', qtd: 5, pct: 17.9, pct_acumulado: 85.7 },
      { pilar: 'Medição', qtd: 4, pct: 14.3, pct_acumulado: 100.0 },
    ],
    ncs_com_causa: 28,
    ncs_total: 32,
  },
  sgq_riscos: { alteracao_sgq: 5, reavaliacao_riscos: 3, ncs_total: 32 },
  prontidao: {
    score: 75,
    faixa: 'ambar',
    componentes: [
      { chave: 'prazos', label: 'Ações no prazo', peso: 40, num: 6, den: 9, pct: 66.7, incluido: true },
      { chave: 'eficacia', label: 'Eficácia verificada', peso: 40, num: 3, den: 4, pct: 75.0, incluido: true },
      { chave: 'causa', label: 'Causa raiz documentada', peso: 20, num: 26, den: 28, pct: 92.9, incluido: true },
    ],
  },
};

function DashboardPage({ clienteId, user, onDrill, onSelectNC, onNovaNC }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // #209: incrementa pra re-disparar o load (botão "Tentar novamente").
  const [retryKey, setRetryKey] = useState(0);
  // #233: demo mode — vê o painel preenchido com dados fictícios (onboarding).
  const [demoMode, setDemoMode] = useState(false);

  // #114: preferência "Painel como tela inicial" (localStorage, por dispositivo).
  const [isHome, setIsHome] = useState(() => {
    try { return localStorage.getItem('qaasa_home_view') === 'dashboard'; } catch (e) { return false; }
  });
  const toggleHome = () => {
    try {
      if (isHome) {
        localStorage.removeItem('qaasa_home_view');
        setIsHome(false);
        showSuccess('Tela inicial voltou ao padrão (lista de NCs).');
      } else {
        localStorage.setItem('qaasa_home_view', 'dashboard');
        setIsHome(true);
        showSuccess('Pronto! O Painel será sua tela inicial ao entrar.');
      }
    } catch (e) { /* localStorage indisponível — ignora silenciosamente */ }
  };

  // #160: quadros ocultos (por dispositivo). MESMO molde do isHome — leitura do
  // localStorage com try/catch. { id: true } = OCULTO; ausência = tudo visível.
  const [blocosOcultos, setBlocosOcultos] = useState(() => {
    try {
      const raw = localStorage.getItem(PREF_BLOCOS_KEY);
      const obj = raw ? JSON.parse(raw) : {};
      return obj && typeof obj === 'object' ? obj : {};
    } catch (e) { return {}; }
  });
  const toggleBloco = (id) => {
    setBlocosOcultos((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      if (!next[id]) delete next[id]; // visível = remove a chave (mantém o objeto enxuto)
      try { localStorage.setItem(PREF_BLOCOS_KEY, JSON.stringify(next)); } catch (e) { /* ignora */ }
      return next;
    });
    showSuccess('Painel atualizado.');
  };
  const mostrar = (id) => !blocosOcultos[id];

  useEffect(() => {
    let cancelado = false;
    async function load() {
      if (!clienteId) { setLoading(false); return; }
      setLoading(true);
      setError(null);
      try {
        // #209: sem timeout, o cold-start do backend deixava o "Carregando
        // painel…" girando PARA SEMPRE (a request nunca rejeita). 20s →
        // estado de erro com "Tentar novamente".
        const d = await Promise.race([
          ncService.getPainel(clienteId),
          new Promise((_, rej) => setTimeout(() => rej(new Error('timeout-painel')), 20000)),
        ]);
        if (!cancelado) setData(d);
      } catch (err) {
        if (!cancelado) {
          const msg = err.message === 'timeout-painel'
            ? 'O painel está demorando para responder — o servidor pode estar iniciando. Tente novamente em instantes.'
            : (err.response?.data?.detail || 'Erro ao carregar o painel');
          setError(msg);
          if (err.message !== 'timeout-painel') showError(msg);
        }
      } finally {
        if (!cancelado) setLoading(false);
      }
    }
    load();
    return () => { cancelado = true; };
  }, [clienteId, retryKey]);

  if (loading) {
    return (
      <div className="dashboard-container">
        <PageHeader kicker="Visão geral" title="Painel" />
        <Card><StateBlock kind="loading" message="Carregando painel…" /></Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-container">
        <PageHeader kicker="Visão geral" title="Painel" />
        <Card>
          <StateBlock kind="error" title="Não foi possível carregar" message={error} />
          <div style={{ textAlign: 'center', marginTop: '8px' }}>
            <Button icon="refresh" onClick={() => setRetryKey((k) => k + 1)}>Tentar novamente</Button>
          </div>
        </Card>
      </div>
    );
  }

  // #233: no demo mode, a fonte vira dados FICTÍCIOS (banner deixa explícito).
  const d = demoMode ? DEMO_DATA : (data || {});
  // navegação inerte na demo (não abrir/filtrar NCs que não existem).
  const _noop = () => {};
  const dDrill = demoMode ? _noop : (onDrill || _noop);
  const dSelect = demoMode ? _noop : (onSelectNC || _noop);
  // conta esparsa REAL (0 ou 1 NC) → oferece o demo mode no header.
  const contaEsparsa = ((data && data.ultimas_5) || []).length <= 1;
  const pendentes = d.pendentes_total || 0;
  const criticas = d.criticas || 0;
  const emAtraso = d.em_atraso || 0;
  const ultimas = d.ultimas_5 || [];
  const fornecedores = d.top_5_fornecedores || [];

  // #237: selo de prontidão do processo (audit readiness). score null = sem base
  // → empty-state. componentes traz peso/num/den/pct de cada fator (transparência).
  const prontidao = d.prontidao || {};
  const prontidaoScore = prontidao.score;          // 0-100 ou null
  const prontidaoFaixa = prontidao.faixa;          // verde/ambar/vermelho ou null
  const prontidaoComponentes = prontidao.componentes || [];

  // Estado vazio total: tenant sem nenhuma NC ainda.
  const semHistorico = pendentes === 0 && ultimas.length === 0 && fornecedores.length === 0;
  if (semHistorico) {
    return (
      <div className="dashboard-container">
        <PageHeader kicker="Visão geral" title="Painel" />
        <Card>
          <StateBlock
            kind="empty"
            icon="dashboard"
            title="Seu painel aparece aqui"
            message="Assim que você registrar a primeira não-conformidade, este painel mostra o que demanda ação, as últimas NCs e onde a dor se concentra."
            action={
              <div className="dashboard-empty-actions">
                {onNovaNC && (
                  <Button icon="add" onClick={onNovaNC}>Registrar primeira NC</Button>
                )}
                {/* #233: vê o painel preenchido (dados fictícios) antes de ter dado real */}
                <Button variant="secondary" icon="science" onClick={() => setDemoMode(true)}>
                  Ver painel de demonstração
                </Button>
              </div>
            }
          />
        </Card>
      </div>
    );
  }

  const maxQtd = fornecedores.reduce((m, f) => Math.max(m, f.qtd || 0), 0) || 1;

  // #52-financeiro: bloco de custo (SÓ manual #51, SEM IA). custo_total === null =
  // abaixo do threshold (≥3 NCs c/ custo) → empty-state educativo; === 0.0 com
  // cobertura ≥3 → mostra "R$ 0,00" honestamente (não cai no empty-state).
  const financeiro = d.financeiro || {};
  const custoTotal = financeiro.custo_total;
  const ncsComCusto = financeiro.ncs_com_custo || 0;
  const ncsTotalPeriodo = financeiro.ncs_total_periodo || 0;
  const maisCaras = financeiro.mais_caras || [];

  // #176: eficácias a avaliar (fecha o ciclo). {total, vencidas} — só dado existente,
  // mesmo critério do lembrete por email (#104). Bloco só aparece quando há pendência.
  const eficacia = d.eficacia || {};
  const eficaciaTotal = eficacia.total || 0;
  const eficaciaVencidas = eficacia.vencidas || 0;

  // #108: distribuição por categoria de impacto (manual, multi-seleção). A soma
  // das contagens pode passar do total de NCs (uma NC marca +1) — honesto.
  const impacto = d.impacto || {};
  const impactoContagem = impacto.contagem || {};
  const ncsComImpacto = impacto.ncs_com_impacto || 0;
  const impactoTotalPeriodo = impacto.ncs_total_periodo || 0;
  const CATS_IMPACTO = [
    { value: 'financeiro', label: 'Financeiro' },
    { value: 'reputacional', label: 'Reputacional' },
    { value: 'operacional', label: 'Operacional' },
  ];
  const impactoMax = Math.max(1, ...CATS_IMPACTO.map((c) => impactoContagem[c.value] || 0));

  // #236: Pareto de causas por pilar Ishikawa (6M). pareto=[] abaixo do
  // threshold (<5 NCs com causa) → empty-state. Cobertura honesta (N de M).
  // Sem IA — só dado existente (modo Investigação). Barras informativas (sem
  // drill: a listagem ainda não filtra por pilar).
  const causas = d.causas || {};
  const causasPareto = causas.pareto || [];
  const ncsComCausa = causas.ncs_com_causa || 0;
  const ncsTotalCausa = causas.ncs_total || 0;
  const maxCausaQtd = causasPareto.reduce((m, c) => Math.max(m, c.qtd || 0), 0) || 1;

  // #159: surfacing SGQ/Riscos (ISO 10.2.1 e2). Informativo; empty-state honesto quando 0.
  const sgqRiscosBloco = d.sgq_riscos || {};
  const sgqAlteracao = sgqRiscosBloco.alteracao_sgq || 0;
  const sgqRiscos = sgqRiscosBloco.reavaliacao_riscos || 0;
  const sgqTotal = sgqRiscosBloco.ncs_total || 0;
  // "Poucos vitais" do Pareto: pilares até cruzar 80% do acumulado → frase de
  // concentração (o insight 80/20). Só aparece se houver concentração real
  // (conjunto vital menor que o total de pilares).
  let resumoPareto = null;
  if (causasPareto.length >= 2) {
    const vitais = [];
    for (const c of causasPareto) {
      vitais.push(c);
      if ((c.pct_acumulado || 0) >= 80) break;
    }
    if (vitais.length < causasPareto.length) {
      const nomes = vitais.map((v) => v.pilar);
      const lista = nomes.length === 1
        ? nomes[0]
        : `${nomes.slice(0, -1).join(', ')} e ${nomes[nomes.length - 1]}`;
      const pct = Math.round(vitais[vitais.length - 1].pct_acumulado || 0);
      resumoPareto = nomes.length === 1
        ? `O pilar ${lista} concentra ${pct}% das causas.`
        : `Os pilares ${lista} concentram ${pct}% das causas.`;
    }
  }

  return (
    <div className="dashboard-container">
      <PageHeader
        kicker="Visão geral"
        title="Painel"
        actions={
          <>
            {/* #233: demo mode — só pra conta esparsa (0-1 NC) ou pra sair da demo. */}
            {(demoMode || contaEsparsa) && (
              <Button
                variant="secondary"
                size="sm"
                icon={demoMode ? 'close' : 'science'}
                onClick={() => setDemoMode(!demoMode)}
                title={demoMode ? 'Voltar aos seus dados reais' : 'Ver o painel preenchido com dados fictícios'}
              >
                {demoMode ? 'Sair da demonstração' : 'Ver demonstração'}
              </Button>
            )}
            <Button
              variant="secondary"
              size="sm"
              icon="home"
              onClick={toggleHome}
              title={isHome ? 'Voltar ao padrão (abrir na lista de NCs)' : 'Abrir o Painel automaticamente ao entrar'}
            >
              {isHome ? 'É sua tela inicial' : 'Definir como tela inicial'}
            </Button>
            {/* #160: "Personalizar" — esconder/mostrar quadros do painel.
                Sem Dropdown no design system → <details>/<summary> nativo (mesmo
                precedente do "Como é calculada?" da Prontidão). Summary estilizado
                pra parecer um Button secondary sm. Checkbox marcado = quadro visível. */}
            <details className="dashboard-personalizar">
              <summary
                className="dashboard-personalizar-trigger"
                title="Escolher quais quadros aparecem no painel"
              >
                <span className="material-symbols-outlined">tune</span>
                <span>Personalizar</span>
              </summary>
              <div className="dashboard-personalizar-menu" role="menu">
                <p className="dashboard-personalizar-head">Quadros do painel</p>
                {BLOCOS_CFG.map((b) => (
                  <label key={b.id} className="dashboard-personalizar-item">
                    <input
                      type="checkbox"
                      checked={mostrar(b.id)}
                      onChange={() => toggleBloco(b.id)}
                    />
                    <span>{b.label}</span>
                  </label>
                ))}
              </div>
            </details>
          </>
        }
      />

      {/* #233: banner explícito de demonstração — não-enganar (dados fictícios). */}
      {demoMode && (
        <div className="dashboard-demo-banner">
          <span className="material-symbols-outlined">science</span>
          <span>
            <strong>Demonstração</strong> — estes números são fictícios, só pra você ver o painel
            preenchido. Seus dados reais voltam ao sair.
          </span>
          <button className="dashboard-demo-sair" onClick={() => setDemoMode(false)}>Sair</button>
        </div>
      )}

      {/* "O que fazer agora?" — ações imediatas contextuais (só dados reais, só quando há algo urgente) */}
      {!demoMode && (emAtraso > 0 || eficaciaVencidas > 0 || criticas > 0) && (
        <Card className="dashboard-agora">
          <div className="dashboard-agora-head">
            <span className="material-symbols-outlined">task_alt</span>
            <span>O que fazer agora?</span>
          </div>
          <ul className="dashboard-agora-lista">
            {emAtraso > 0 && (
              <li className="dashboard-agora-item">
                <span className="material-symbols-outlined dashboard-agora-icon--atraso">alarm</span>
                <span className="dashboard-agora-item-texto">
                  <strong>{emAtraso} {emAtraso === 1 ? 'NC em atraso' : 'NCs em atraso'}</strong> — revise e atualize as ações pendentes.
                </span>
                {onDrill && <Button size="sm" variant="secondary" onClick={() => dDrill({ em_atraso: true })}>Ver</Button>}
              </li>
            )}
            {eficaciaVencidas > 0 && (
              <li className="dashboard-agora-item">
                <span className="material-symbols-outlined dashboard-agora-icon--eficacia">verified</span>
                <span className="dashboard-agora-item-texto">
                  <strong>{eficaciaVencidas} {eficaciaVencidas === 1 ? 'verificação de eficácia vencida' : 'verificações de eficácia vencidas'}</strong> — registre o resultado.
                </span>
                {onDrill && <Button size="sm" variant="secondary" onClick={() => dDrill({ eficacia: 'a_avaliar' })}>Ver</Button>}
              </li>
            )}
            {criticas > 0 && emAtraso === 0 && (
              <li className="dashboard-agora-item">
                <span className="material-symbols-outlined dashboard-agora-icon--critica">priority_high</span>
                <span className="dashboard-agora-item-texto">
                  <strong>{criticas} {criticas === 1 ? 'NC crítica pendente' : 'NCs críticas pendentes'}</strong> — priorize o tratamento.
                </span>
                {onDrill && <Button size="sm" variant="secondary" onClick={() => dDrill({ demanda_acao: true, criticas: true })}>Ver</Button>}
              </li>
            )}
          </ul>
        </Card>
      )}

      {/* #176-polish: blocos acionáveis no topo, lado a lado. O selo de Prontidão
          (#237) entra como 1º card desta row pra ficar do MESMO tamanho dos demais
          (em vez de full-width esticado). Flex — dividem a largura igualmente;
          empilha no mobile. Sem eficácia pendente, ficam selo + hero na linha. */}
      <div className="dashboard-trio">
      {/* Selo de Prontidão do processo de NC (#237). Resume o ESTADO do sistema
          ("se o auditor chegar hoje, estou em ordem?"). 100% transparente — mostra
          peso + fração de cada fator e um "Como é calculada?". score null = empty-state. */}
      {mostrar('prontidao') && (
      <Card className={`dashboard-prontidao dashboard-prontidao--${prontidaoFaixa || 'sembase'}`}>
        {prontidaoScore == null ? (
          <StateBlock
            kind="empty"
            icon="verified_user"
            title="Prontidão para auditoria"
            message="Aparece aqui conforme você trata as NCs — ações no prazo, eficácia verificada e causa raiz documentada."
          />
        ) : (
          <div className="dashboard-prontidao-inner">
            <div className="dashboard-prontidao-head">
              <div className="dashboard-prontidao-num">
                <span className="dashboard-prontidao-valor">{prontidaoScore}%</span>
                <div>
                  <div className="dashboard-prontidao-label">Prontidão do processo de NC</div>
                  <div className="dashboard-prontidao-faixa">{PRONTIDAO_FAIXA_TXT[prontidaoFaixa] || ''}</div>
                </div>
              </div>
            </div>
            <ul className="dashboard-prontidao-fatores">
              {prontidaoComponentes.map((c) => (
                <li key={c.chave} className={c.incluido ? '' : 'is-fora'}>
                  <div className="dashboard-prontidao-fator-head">
                    <span className="dashboard-prontidao-fator-nome">{c.label}</span>
                    <span className="dashboard-prontidao-fator-peso">peso {c.peso}%</span>
                  </div>
                  {c.incluido ? (
                    <div className="dashboard-prontidao-fator-row">
                      <div className="dashboard-prontidao-fator-bar">
                        <div
                          className="dashboard-prontidao-fator-fill"
                          style={{ width: `${Math.round(c.pct)}%` }}
                        />
                      </div>
                      <span className="dashboard-prontidao-fator-val">{c.num}/{c.den} · {c.pct}%</span>
                    </div>
                  ) : (
                    <span className="dashboard-prontidao-fator-fora">ainda sem base — fora do cálculo</span>
                  )}
                </li>
              ))}
            </ul>
            <details className="dashboard-prontidao-como">
              <summary>Como é calculada?</summary>
              <p>
                A nota é a <strong>média ponderada</strong> dos fatores acima — ações corretivas
                no prazo (peso 40%), eficácia já verificada nas NCs cuja avaliação venceu (40%) e
                causa raiz documentada (20%). Fatores ainda sem base (ex.: nenhuma eficácia chegou
                na data de avaliação) ficam <strong>de fora</strong> e os pesos se redistribuem
                entre os demais. Mede a <strong>disciplina do seu processo</strong> de tratamento
                de NC — não garante aprovação na auditoria.
              </p>
            </details>
          </div>
        )}
      </Card>
      )}
      {/* Bloco 1 — Pendências (hero) — SEMPRE visível (#160 não toggla) */}
      <Card className="dashboard-hero">
        {pendentes === 0 ? (
          <div className="dashboard-tudo-em-dia">
            <span className="material-symbols-outlined dashboard-tudo-icon">check_circle</span>
            <div>
              <div className="dashboard-tudo-titulo">Tudo em dia</div>
              <div className="dashboard-tudo-sub">Nenhuma NC aguardando ação no momento.</div>
            </div>
          </div>
        ) : (
          <>
            <button
              className="dashboard-hero-num"
              onClick={() => dDrill({ demanda_acao: true })}
              title="Ver as NCs que demandam ação"
            >
              <span className="dashboard-hero-valor">{pendentes}</span>
              <span className="dashboard-hero-label">
                {pendentes === 1 ? 'NC aguardando ação' : 'NCs aguardando ação'}
              </span>
            </button>
            <div className="dashboard-hero-pills">
              <button
                className="dashboard-pill dashboard-pill-critica"
                disabled={criticas === 0}
                onClick={() => dDrill({ demanda_acao: true, criticas: true })}
              >
                <span className="material-symbols-outlined">warning</span>
                {criticas} {criticas === 1 ? 'crítica' : 'críticas'}
              </button>
              <button
                className="dashboard-pill dashboard-pill-atraso"
                disabled={emAtraso === 0}
                onClick={() => dDrill({ em_atraso: true })}
              >
                <span className="material-symbols-outlined">schedule</span>
                {emAtraso} em atraso
              </button>
            </div>
          </>
        )}
      </Card>

      {/* Bloco eficácia — Eficácias a avaliar (#176). Fecha o ciclo: NCs com
          avaliação de eficácia pendente (data prevista + status não-terminal +
          não avaliada — mesmo critério do lembrete por email #104). Acionável,
          não educativo → só aparece quando há algo a avaliar. */}
      {mostrar('eficacia') && eficaciaTotal > 0 && (
        <Card className="dashboard-panel dashboard-eficacia">
          <h3 className="dashboard-panel-title">Eficácias a avaliar</h3>
          <button
            className="dashboard-fin-num"
            onClick={() => dDrill({ eficacia: 'a_avaliar' })}
            title="Ver as NCs com avaliação de eficácia pendente"
          >
            <span className="dashboard-fin-valor">{eficaciaTotal}</span>
            <span className="dashboard-fin-label">
              {eficaciaTotal === 1 ? 'NC aguardando avaliação de eficácia' : 'NCs aguardando avaliação de eficácia'}
            </span>
          </button>
          {eficaciaVencidas > 0 && (
            <p className="dashboard-efic-vencidas">
              <span className="material-symbols-outlined">schedule</span>
              {eficaciaVencidas === 1
                ? '1 com prazo de avaliação vencido'
                : `${eficaciaVencidas} com prazo de avaliação vencido`}
            </p>
          )}
        </Card>
      )}

      {/* Bloco SGQ/Riscos (#159) — surfacing das NCs que sinalizaram, na avaliação
          de eficácia, alteração de documento do SGQ ou reavaliação de riscos/
          oportunidades (ISO 10.2.1 e2 / #158). Informativo (read-only); empty-state
          honesto quando ninguém sinalizou ainda (não-enganar). */}
      {mostrar('sgq') && (
      <Card className="dashboard-panel dashboard-sgq">
        <h3 className="dashboard-panel-title">Alterações no SGQ e Riscos</h3>
        {(sgqAlteracao + sgqRiscos) === 0 ? (
          <StateBlock
            kind="empty"
            icon="rule"
            title="Sem sinalizações ainda"
            message="Aparece aqui quando uma NC indica, na avaliação de eficácia, que precisou alterar documento do SGQ ou reavaliar riscos/oportunidades."
          />
        ) : (
          <>
            <div className="dashboard-fin-num" style={{ cursor: 'default' }}>
              <span className="dashboard-fin-valor">{sgqAlteracao}</span>
              <span className="dashboard-fin-label">
                {sgqAlteracao === 1 ? 'NC alterou documento do SGQ' : 'NCs alteraram documento do SGQ'}
              </span>
            </div>
            <div className="dashboard-fin-num" style={{ cursor: 'default', marginTop: '10px' }}>
              <span className="dashboard-fin-valor">{sgqRiscos}</span>
              <span className="dashboard-fin-label">
                {sgqRiscos === 1 ? 'NC gerou reavaliação de riscos' : 'NCs geraram reavaliação de riscos'}
              </span>
            </div>
            <p className="dashboard-efic-vencidas" style={{ marginTop: '10px' }}>
              de {sgqTotal} {sgqTotal === 1 ? 'NC' : 'NCs'} no total
            </p>
          </>
        )}
      </Card>
      )}
      {/* #160-fluxo-único: atalhos (Prontidão/NCs aguardando/Eficácias/SGQ) +
          analíticos (Concentração/Impacto/Causas) ficam no MESMO grid → os
          quadros de baixo "sobem" pra completar a linha de cima, sem vão. */}
        {/* Bloco 3 — Concentração de fornecedores. #124 (#127): quando o
            backend ainda não tem dados suficientes (threshold <10 NCs com
            fornecedor / 90d), em vez de SUMIR com o bloco, mostramos um
            empty-state — assim o painel preserva o layout de 3 blocos. */}
        {mostrar('fornecedores') && (
        <Card className="dashboard-panel">
          <h3 className="dashboard-panel-title">Concentração de fornecedores</h3>
          {fornecedores.length === 0 ? (
            <StateBlock
              kind="empty"
              icon="local_shipping"
              message="Ainda sem dados suficientes pra mostrar a concentração — aparece quando houver mais NCs com fornecedor."
            />
          ) : (
            <>
              <p className="dashboard-panel-sub">NCs por fornecedor — últimos 90 dias</p>
              <ul className="dashboard-conc">
                {fornecedores.map((f) => (
                  <li key={f.id}>
                    <button
                      className="dashboard-conc-row"
                      onClick={() => dDrill({ fornecedor_id: f.id })}
                      title={`Ver NCs de ${f.nome}`}
                    >
                      <div className="dashboard-conc-head">
                        <span className="dashboard-conc-nome">{f.nome}</span>
                        <span className="dashboard-conc-qtd">{f.qtd}</span>
                      </div>
                      <div className="dashboard-conc-barra">
                        <div
                          className="dashboard-conc-barra-fill"
                          style={{ width: `${Math.round((f.qtd / maxQtd) * 100)}%` }}
                        />
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </Card>
        )}

        {/* Bloco 5 — Impacto por categoria (#108). Manual (sem IA), multi-seleção
          → a soma pode passar do total de NCs (cada NC pode marcar +1). Cada
          categoria dá drill pra listagem filtrada. Empty-state educativo. */}
      {mostrar('impacto') && (
      <Card className="dashboard-panel dashboard-impacto">
        <h3 className="dashboard-panel-title">Impacto por categoria</h3>
        {ncsComImpacto === 0 ? (
          <StateBlock
            kind="empty"
            icon="category"
            message="Classifique o impacto de cada NC (financeiro, reputacional, operacional) ao editá-la. A distribuição aparece aqui."
            action={onDrill && (
              <Button variant="secondary" icon="receipt_long" onClick={() => dDrill({})}>
                Ver minhas NCs
              </Button>
            )}
          />
        ) : (
          <>
            {CATS_IMPACTO.map((c) => {
              const n = impactoContagem[c.value] || 0;
              return (
                <button
                  key={c.value}
                  className="dashboard-impacto-row"
                  onClick={() => onDrill && dDrill({ categoria_impacto: c.value })}
                  title={`Ver NCs com impacto ${c.label.toLowerCase()}`}
                  disabled={n === 0}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '10px', width: '100%',
                    background: 'transparent', border: 'none', padding: '6px 0',
                    cursor: n === 0 ? 'default' : 'pointer', textAlign: 'left',
                  }}
                >
                  <span style={{ minWidth: '92px', fontSize: '13px', color: '#334155' }}>{c.label}</span>
                  <span style={{ flex: 1, height: '8px', background: '#eef2f7', borderRadius: '4px', overflow: 'hidden' }}>
                    <span style={{ display: 'block', height: '100%', width: `${Math.round((n / impactoMax) * 100)}%`, background: '#0d7c3f', borderRadius: '4px' }} />
                  </span>
                  <span style={{ minWidth: '24px', textAlign: 'right', fontWeight: 600, fontSize: '13px', color: '#0f172a' }}>{n}</span>
                </button>
              );
            })}
            <p className="dashboard-fin-cobertura">
              em {ncsComImpacto} de {impactoTotalPeriodo} {impactoTotalPeriodo === 1 ? 'NC' : 'NCs'} classificada{ncsComImpacto === 1 ? '' : 's'} no trimestre
            </p>
          </>
        )}
      </Card>
      )}

      {/* Bloco 6 — Causas mais frequentes (#236). Pareto por pilar do Ishikawa
          (6M) — só NCs em modo Investigação têm 6M. Abaixo de 5 NCs com causa,
          pareto=[] → empty-state educativo (também nudge pró-Investigação).
          Barras informativas (a listagem ainda não filtra por pilar → sem drill).
          Cobertura honesta (N de M); frase de concentração entrega o 80/20. */}
      {mostrar('causas') && (
      <Card className="dashboard-panel dashboard-causas">
        <h3 className="dashboard-panel-title">Causas mais frequentes</h3>
        {causasPareto.length === 0 ? (
          <StateBlock
            kind="empty"
            icon="account_tree"
            message="As causas se concentram aqui conforme você investiga as NCs (modo Investigação, Ishikawa 6M). Aparece quando 5 ou mais NCs tiverem causa identificada."
          />
        ) : (
          <>
            <p className="dashboard-panel-sub">
              por pilar do Ishikawa (6M) — em {ncsComCausa} de {ncsTotalCausa} {ncsTotalCausa === 1 ? 'NC' : 'NCs'} com causa investigada
            </p>
            <ul className="dashboard-conc">
              {causasPareto.map((c) => (
                <li key={c.pilar}>
                  <div className="dashboard-conc-row dashboard-conc-row--static">
                    <div className="dashboard-conc-head">
                      <span className="dashboard-conc-nome">{c.pilar}</span>
                      <span className="dashboard-conc-qtd">{c.qtd} · {c.pct}%</span>
                    </div>
                    <div className="dashboard-conc-barra">
                      <div
                        className="dashboard-conc-barra-fill"
                        style={{ width: `${Math.round((c.qtd / maxCausaQtd) * 100)}%` }}
                      />
                    </div>
                  </div>
                </li>
              ))}
            </ul>
            {resumoPareto && <p className="dashboard-pareto-resumo">{resumoPareto}</p>}
          </>
        )}
      </Card>
      )}
      </div>{/* /grid único dos quadros menores (#160-fluxo-único) */}

      {/* Dupla (1/2 cada no desktop) — os dois cards "ricos" em lista:
          Impacto financeiro · Últimas NCs. Empilha no mobile (1 coluna). */}
      <div className="dashboard-grid">
        {/* Bloco 4 — Impacto financeiro (#52-financeiro). SÓ custo MANUAL (#51), sem IA.
            custo_total === null → abaixo do threshold (≥3 NCs c/ custo) → empty-state
            educativo. === 0.0 com cobertura → mostra "R$ 0,00" honestamente. */}
        {mostrar('financeiro') && (
        <Card className="dashboard-panel dashboard-financeiro">
          <h3 className="dashboard-panel-title">Impacto financeiro</h3>
          {custoTotal == null ? (
            <StateBlock
              kind="empty"
              icon="payments"
              message="Veja quanto a não-qualidade custou no trimestre. Preencha o custo estimado ao editar uma NC — quando 3 ou mais tiverem custo, o total aparece aqui."
              action={onDrill && (
                <Button variant="secondary" icon="receipt_long" onClick={() => dDrill({})}>
                  Ver minhas NCs
                </Button>
              )}
            />
          ) : (
            <>
              <button
                className="dashboard-fin-num"
                onClick={() => dDrill({ tem_custo: true })}
                title="Ver as NCs com custo informado"
              >
                <span className="dashboard-fin-valor">R$ {formatCurrencyBR(custoTotal)}</span>
                <span className="dashboard-fin-label">custo estimado da não-qualidade (trimestre)</span>
              </button>
              <p className="dashboard-fin-cobertura">
                em {ncsComCusto} de {ncsTotalPeriodo} {ncsTotalPeriodo === 1 ? 'NC' : 'NCs'} com custo informado
              </p>
              {maisCaras.length > 0 && (
                <>
                  <p className="dashboard-panel-sub">NCs mais caras</p>
                  <ul className="dashboard-fin-lista">
                    {maisCaras.map((nc) => (
                      <li key={nc.id}>
                        <button className="dashboard-fin-row" onClick={() => dSelect(nc.id)}>
                          <div className="dashboard-fin-row-top">
                            <span className="dashboard-ultima-num">{nc.numero_nc}</span>
                            <span className="dashboard-fin-row-custo">R$ {formatCurrencyBR(nc.custo_estimado)}</span>
                          </div>
                          <div className="dashboard-ultima-desc">{nc.descricao}</div>
                          <OrigemBadge nc={nc} />
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </>
          )}
        </Card>
        )}

        {/* Bloco 2 — Últimas 5 NCs — SEMPRE visível (#160 não toggla) */}
        <Card className="dashboard-panel">
          <h3 className="dashboard-panel-title">Últimas NCs</h3>
          {ultimas.length === 0 ? (
            <p className="dashboard-vazio">Nenhuma NC ainda.</p>
          ) : (
            <ul className="dashboard-ultimas">
              {ultimas.map((nc) => (
                <li key={nc.id}>
                  <button className="dashboard-ultima-row" onClick={() => dSelect(nc.id)}>
                    <div className="dashboard-ultima-top">
                      <span className="dashboard-ultima-num">{nc.numero_nc}</span>
                      <OrigemBadge nc={nc} />
                    </div>
                    <div className="dashboard-ultima-desc">{nc.descricao}</div>
                    <div className="dashboard-ultima-data">{tempoRelativo(nc.created_at)}</div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>{/* /dupla — Impacto financeiro · Últimas NCs */}
    </div>
  );
}

export default DashboardPage;
